import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface UserPreferences {
  theme: 'light' | 'dark';
  language: 'en' | 'es';
  timezone?: string;
  calendarStartDay?: 'monday' | 'sunday';
  updatedAt: Date;
}

/**
 * Gets user preferences from Firestore
 */
export async function getUserPreferences(userId: string): Promise<UserPreferences | null> {
  try {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.preferences) {
        return {
          theme: data.preferences.theme || 'light',
          language: data.preferences.language || 'en',
          timezone: data.preferences.timezone,
          calendarStartDay: data.preferences.calendarStartDay || 'monday',
          updatedAt: data.preferences.updatedAt?.toDate() || new Date(),
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting user preferences:', error);
    return null;
  }
}

/**
 * Updates user preferences in Firestore
 */
export async function updateUserPreferences(
  userId: string,
  preferences: Partial<Pick<UserPreferences, 'theme' | 'language' | 'timezone' | 'calendarStartDay'>>
): Promise<void> {
  try {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);

    const preferencesData = {
      ...preferences,
      updatedAt: new Date(),
    };

    if (docSnap.exists()) {
      // Update existing document
      await updateDoc(docRef, {
        preferences: preferencesData,
      });
    } else {
      // Create new document with preferences
      await setDoc(docRef, {
        preferences: preferencesData,
        displayName: '',
        email: '',
      });
    }
  } catch (error) {
    console.error('Error updating user preferences:', error);
    throw new Error('Failed to update user preferences');
  }
}

/**
 * Gets theme preference with fallback to system preference
 */
function getSystemTheme(): 'light' | 'dark' {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/**
 * Gets language preference with fallback to browser language
 */
function getSystemLanguage(): 'en' | 'es' {
  if (typeof window !== 'undefined' && navigator.language) {
    const lang = navigator.language.toLowerCase();
    if (lang.startsWith('es')) {
      return 'es';
    }
  }
  return 'en';
}

/**
 * Gets stored theme from localStorage with fallback to system preference
 */
export function getStoredTheme(): 'light' | 'dark' {
  try {
    const stored = localStorage.getItem('theme') as 'light' | 'dark' | null;
    if (stored && (stored === 'light' || stored === 'dark')) {
      return stored;
    }
  } catch (error) {
    console.error('Error reading theme from localStorage:', error);
  }
  return getSystemTheme();
}

/**
 * Gets stored language from localStorage with fallback to system preference
 */
export function getStoredLanguage(): 'en' | 'es' {
  try {
    const stored = localStorage.getItem('i18nextLng') as 'en' | 'es' | null;
    if (stored && (stored === 'en' || stored === 'es')) {
      return stored;
    }
  } catch (error) {
    console.error('Error reading language from localStorage:', error);
  }
  return getSystemLanguage();
}

/**
 * Stores theme in localStorage
 */
export function storeTheme(theme: 'light' | 'dark'): void {
  try {
    localStorage.setItem('theme', theme);
  } catch (error) {
    console.error('Error storing theme in localStorage:', error);
  }
}

/**
 * Stores language in localStorage
 */
export function storeLanguage(language: 'en' | 'es'): void {
  try {
    localStorage.setItem('i18nextLng', language);
  } catch (error) {
    console.error('Error storing language in localStorage:', error);
  }
}
